import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import currency from 'currency.js'
import moment from 'moment'

import { selectContractorByUuid } from '../../payroll.selectors'
import {
  getContractorName,
  getContractorWageText,
  getMinPayDate,
} from '../../helpers'
import HourlyCell from '../payroll-input/HourlyCell'
import FixedCell from '../payroll-input/FixedCell'
import { ContractorPayrollHeader } from './ContractorPayrollHeader'
import { LastContractorPayments } from './LastContractorPayments'
import { formatCurrency } from '@/utils/currencyHelpers'
import { Button, DatePicker, Icon, Separator, Table, Text } from '@/ui'
import { FlsaStatus } from '../../employeeJob.slice'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'

export const ContPayrollPaymentInputTab = ({
  contractorUuid,
  payDate: initPayDate,
  hours: initHours,
  bonus: initBonus,
  reimbursement: initReimbursement,
  wage: initWage,
  goForward,
  goBack,
}: {
  contractorUuid: string
  payDate: string
  hours: string
  bonus: string
  wage: string
  reimbursement: string
  goForward: (_: {
    payDate: string
    hours?: string
    wage?: string
    bonus: string
    reimbursement: string
  }) => void
  goBack: () => void
}) => {
  const hoursRef = useRef<HourlyCell>(null)
  const bonusRef = useRef<FixedCell>(null)
  const reimRef = useRef<FixedCell>(null)
  const wageRef = useRef<FixedCell>(null)
  const contractor = useReselector(selectContractorByUuid, contractorUuid)
  const [payDate, setPayDate] = useState(
    initPayDate ? moment(initPayDate).format(DATE_FORMATS.INPUT) : ''
  )
  const [total, setTotal] = useState(0)

  // User payment configs don't affect contractors.  It will always be 4 for contractors with direct deposit
  const minPayDate = useMemo(() => getMinPayDate(4).toJSDate(), [])

  // We're using the standard payroll's components which use event listeners.  It's a little overkill for contractor
  // payments since there is only one row but it's cleaner to use the same components instead of duplicating them
  const valueChanged = useCallback(() => {
    if (!contractor?.uuid) {
      return
    }
    setTotal(
      currency(contractor.hourly_rate || 0)
        .multiply(hoursRef.current?.calculate().hours || 0)
        .add(bonusRef.current?.calculate().amount || 0)
        .add(reimRef.current?.calculate().amount || 0)
        .add(wageRef.current?.calculate().amount || 0).value
    )

    // Not sure why the refs aren't dependencies since their existence directly effects the result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contractor?.hourly_rate,
    contractor?.uuid,
    hoursRef.current,
    bonusRef.current,
    reimRef.current,
    wageRef.current,
  ])

  useEffect(() => {
    document.addEventListener(`inputChange${contractorUuid}`, valueChanged)

    return () => {
      document.removeEventListener(`inputChange${contractorUuid}`, valueChanged)
    }
  }, [contractorUuid, valueChanged])

  // Set the initial total value
  useEffect(() => {
    valueChanged()
  }, [valueChanged])

  useEffect(() => {
    if (!payDate && minPayDate) {
      setPayDate(moment(minPayDate).format(DATE_FORMATS.INPUT))
    }
  }, [minPayDate, payDate])

  const submit = useCallback(() => {
    goForward({
      payDate: moment(payDate, DATE_FORMATS.INPUT).format(
        DATE_FORMATS.GUSTO_SUBMIT
      ),
      hours: hoursRef.current?.calculate().hours,
      wage: wageRef.current?.calculate().amount,
      bonus: bonusRef.current?.calculate().amount || '',
      reimbursement: reimRef.current?.calculate().amount || '',
    })
  }, [goForward, payDate])

  if (!contractor) {
    return null
  }

  const isHourly = contractor.wage_type === 'Hourly'

  return (
    <div className="flex flex-col gap-6">
      <ContractorPayrollHeader currentTab={1} />

      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-2/3 flex flex-col gap-4">
          <Text variant={TypographyStyles.H1}>
            Select a date to pay {getContractorName(contractor)}
          </Text>

          {minPayDate && (
            <DatePicker
              value={payDate}
              onChange={setPayDate}
              minDate={minPayDate}
              placeholder="Please enter a date"
            />
          )}
        </div>
        <div className="w-full md:w-1/3">
          <LastContractorPayments contractorUuid={contractorUuid} />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.H3}>
          {isHourly ? 'Enter hours and payments' : 'Enter payments'}
        </Text>

        <Text>
          To pay this contractor, please enter the{' '}
          {isHourly ? 'number of hours worked.' : 'amount of wages owed.'}
        </Text>
      </div>

      <div className="overflow-auto">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="w-1/6">Contractor</Table.HeaderCell>
              <Table.HeaderCell className="w-2/6">
                {isHourly && (
                  <>
                    Hours (H)
                    <br />
                    <br />
                  </>
                )}
                Bonus (B)
              </Table.HeaderCell>
              <Table.HeaderCell className="w-2/6">
                {!isHourly && (
                  <>
                    Wage (W)
                    <br />
                    <br />
                  </>
                )}
                Reimbursement (R)
              </Table.HeaderCell>
              <Table.HeaderCell className="w-1/6">Total</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <b>{getContractorName(contractor, true)}</b>
                <br />
                {getContractorWageText(contractor)}
              </Table.Cell>
              <Table.Cell>
                {isHourly && (
                  <HourlyCell
                    employeeUuid={contractorUuid}
                    hourlyComp={{
                      name: 'Regular',
                      hours: initHours,
                      job_uuid: '',
                      compensation_multiplier: 1,
                      flsa_status: FlsaStatus.Nonexempt,
                    }}
                    ref={hoursRef}
                  />
                )}
                <FixedCell
                  employeeUuid={contractorUuid}
                  fixedComp={{
                    name: 'Bonus',
                    amount: initBonus,
                    job_uuid: '',
                  }}
                  ref={bonusRef}
                />
              </Table.Cell>
              <Table.Cell>
                {!isHourly && (
                  <FixedCell
                    employeeUuid={contractorUuid}
                    fixedComp={{
                      name: 'Wage',
                      amount: initWage,
                      job_uuid: '',
                    }}
                    ref={wageRef}
                  />
                )}
                <FixedCell
                  employeeUuid={contractorUuid}
                  fixedComp={{
                    name: 'Reimbursement',
                    amount: initReimbursement,
                    job_uuid: '',
                  }}
                  ref={reimRef}
                />
              </Table.Cell>
              <Table.Cell>
                <b>{formatCurrency(total)}</b>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-4">
        <div className="w-full md:w-1/4">
          <Button variant="secondary" onClick={goBack} className="w-full">
            Go Back
          </Button>
        </div>
        <div className="hidden md:block md:w-1/2" />
        <div className="w-full md:w-1/4">
          <Button onClick={submit} className="w-full">
            Continue <Icon icon={faArrowRight} />
          </Button>
        </div>
      </div>
    </div>
  )
}
